import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const onboarding = {
    header: {
        backA11y: 'Back',
        skip: 'Skip',
    },
    welcome: {
        headline: "Control your spending.\nIt's that easy.",
        subtitle: 'Answer 3 questions and Kontto sets itself up your way.',
        startButton: 'Set up my Kontto',
        effortNote: 'Takes under 1 minute · no sign-up',
        restoreButton: 'I already use Kontto · Restore',
        restoreA11y: 'I already use Kontto, restore',
        enterError: 'Failed to enter the app',
    },
    goal: {
        title: 'What do you want to tackle first?',
        subtitle: "Pick as many as you like. We'll build your Kontto around it.",
        promisePair: '{{first}} and {{second}}',
        promiseOverflow: '{{first}}, {{second}} and {{extra}} more',
        promisePrefix: 'Your Kontto will prioritize ',
        promiseSuffix: '.',
        continueButton: 'Set up my Kontto',
    },
    account: {
        title: 'Where do you spend from?',
        subtitle: "Pick one to start — it's just to identify the account, nothing is connected to your bank.",
        searchPlaceholder: 'Search bank',
        cashChip: 'Cash',
        otherChip: 'Other',
        searchEmpty: 'No bank matching "{{query}}". Tap Skip to use a cash wallet.',
        continueButton: 'Continue',
        createError: 'Failed to create the account',
    },
    starter: {
        skip: 'Skip',
        eyebrow: 'LAST STEP',
        title: 'What was your\nlast expense?',
        subtitle: 'Guess the amount — you can edit it later.',
        categoryQuestion: 'On what?',
        moreCategories: 'More',
        moreCategoriesA11y: 'More categories',
        saveButton: 'Save my 1st expense',
        saveA11y: 'Save first expense',
        saveError: "Couldn't save",
    },
    done: {
        headline: "All set. You're in control 🎉",
        subtitle: "Your Kontto is set up your way — no sign-up, on your device. Let's get started.",
        reminders: {
            title: 'Turn on reminders',
            description: "Due-date and inactivity alerts so you don't miss a bill.",
            button: 'Turn on reminders',
            grantedTitle: 'Reminders on',
            grantedMessage: "We'll remind you before each due date.",
            deniedTitle: 'No problem',
            deniedMessage: 'You can turn them on anytime in Settings › Reminders.',
        },
        pro: {
            title: 'Meet Kontto Pro',
            description: 'Advanced reports, no limits, and more — take a look whenever you like.',
        },
        enterButton: 'Go to the app',
    },
    focus: {
        reports: { label: 'Where my money goes', planLine: 'Spending by category, in detail', promiseLabel: 'where the money goes' },
        budget: { label: 'Stay within budget', planLine: 'Budgets that alert you before you overspend', promiseLabel: 'staying within budget' },
        bills: { label: 'Bills & statements on time', planLine: 'Due-date alerts for every account', promiseLabel: 'bills on time' },
        goal: { label: 'Save toward a goal', planLine: 'One goal with a clear amount and deadline', promiseLabel: 'your goal' },
        export: { label: 'Everything in a report', planLine: 'A report as PDF and spreadsheet', promiseLabel: 'reports' },
    },
    personalization: {
        'control-spending': { homeHeadline: "Let's keep the month on track: create your first budget.", offerLead: 'To keep close track of your spending' },
        'plan-the-month': { homeHeadline: 'Add your credit card and see where your money goes.', offerLead: 'To see where your money goes' },
        'organize-cards': { homeHeadline: "Let's organize your cards: add your first one.", offerLead: 'To keep your cards and statements under control' },
        'save-for-a-goal': { homeHeadline: "Let's save toward your goal: create the first one.", offerLead: 'To help you save toward your goal' },
        'automate-android': { homeHeadline: 'Turn on automatic capture and Kontto logs for you.', offerLead: 'To log your expenses effortlessly' },
    },
} satisfies DeepPartialStrings<TranslationSchema['onboarding']>;
